import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Grid,
  Typography,
  Table,
  TableHead,
  TableContainer,
  TableBody,
  TableRow,
  TablePagination,
  LinearProgress,
  Checkbox,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
  ClickAwayListener,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import {
  useAddRoleMutation,
  useGetRolesQuery,
  useRemoveRoleMutation,
} from "./rolesApiSlice";
import toast from "../../../helpers/toast";
import { useGetAllUsersQuery } from "../../../app/authApiSlice";
import getErrorMessage from "../../../utils/getErrorMessage";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";

const PER_PAGE = 10;

const roleDescriptions = {
  admin: "Has full access to manage the school, users, and settings.",
  examiner: "Can create, manage, and grade exams and assessments.",
  accountant: "Handles fee payments, invoices, and financial records.",
  reviewer: "Can review and approve content, grades, or applications.",
  webmaster: "Manages the school's website content and configuration.",
};

function RoleTooltip({ role }) {
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box display="inline-flex" alignItems="center">
        <Tooltip
          title={roleDescriptions[role.name.toLowerCase()] || "No description available."}
          arrow
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          disableTouchListener
        >
          <IconButton 
            size="small" 
            sx={{ p: 0 }} 
            onClick={() => setOpen(!open)}
          >
            <HelpOutlineIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}

function Roles() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: rolesResponse, isLoading: isLoadingRoles } = useGetRolesQuery();
  const roles = rolesResponse?.roles || [];

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: usersData, isLoading: isLoadingUsers } = useGetAllUsersQuery({
    school_id: 0,
    includes: ["teacher", "examiner", "admin", "accountant"],
    excludes: ["student"],
    page,
    per_page: PER_PAGE,
    q: debouncedSearch,
  });

  const users = useMemo(() => usersData?.users || [], [usersData]);
  const pagination = usersData?.pagination;

  const [addRole] = useAddRoleMutation();
  const [removeRole] = useRemoveRoleMutation();

  const [roleConfirmState, setRoleConfirmState] = useState({
    open: false,
    row: null,
    role: null,
    hasRole: false,
    key: null,
  });

  const handleRoleChangeClick = (row, role, hasRole, key) => {
    setRoleConfirmState({
      open: true,
      row,
      role,
      hasRole,
      key,
    });
  };

  const handleConfirmRoleChange = async () => {
    const { row, role, hasRole, key } = roleConfirmState;
    const newValue = !hasRole;
    setOptimisticState((prev) => ({ ...prev, [key]: newValue }));
    try {
      let response;
      if (hasRole) {
        response = await removeRole({
          user_id: row.id,
          role: role.name,
        });
      } else {
        response = await addRole({
          user_id: row.id,
          role: role.name,
        });
      }
      if (response.error) {
        setOptimisticState((prev) => ({ ...prev, [key]: !newValue }));
        toast.error(getErrorMessage(response));
      } else {
        toast.success(`Role ${hasRole ? "removed" : "added"} successfully`);
      }
    } catch (error) {
      setOptimisticState((prev) => ({ ...prev, [key]: !newValue }));
      toast.error(getErrorMessage(error));
    }
  };
  const [optimisticState, setOptimisticState] = useState({});

  // Clear optimistic state when server data catches up
  useEffect(() => {
    if (!users.length) return;
    setOptimisticState((prev) => {
      const newState = { ...prev };
      let changed = false;
      Object.keys(newState).forEach((key) => {
        const [userId, roleName] = key.split("-");
        const user = users.find((u) => u.id === parseInt(userId));
        if (user) {
          const serverHasRole = user.roles?.some(
            (r) => r.role.name === roleName,
          );
          if (serverHasRole === newState[key]) {
            delete newState[key];
            changed = true;
          }
        }
      });
      return changed ? newState : prev;
    });
  }, [users]);

  const displayedRoles = roles.filter(
    (role) =>
      role.name !== "student" &&
      role.name !== "agent" &&
      role.name !== "parent" &&
      role.name !== "teacher" &&
      role.name !== "superadmin",
  );

  return (
    <>
      <Grid container spacing={3}>
        <CustomWarningDialog
          open={roleConfirmState.open}
          setOpen={(open) => setRoleConfirmState((prev) => ({ ...prev, open }))}
          action={handleConfirmRoleChange}
          item={null}
          message={`Are you sure you want to ${
            roleConfirmState.hasRole ? "remove" : "add"
          } the ${roleConfirmState.role?.name} role ${
            roleConfirmState.hasRole ? "from" : "to"
          } ${roleConfirmState.row?.profile?.first_name}?`}
        />
        <Grid item xs={12} md={12} lg={12}>
          <StyledPaper>
            <Typography sx={{ padding: "15px" }} variant="formHeading">
              Current Roles
            </Typography>
            <StyledTextField
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{
                mx: 2,
                mb: 1,
                width: isMobile ? "calc(100% - 32px)" : "300px",
              }}
              size="small"
            />
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {(isLoadingRoles || isLoadingUsers) && <LinearProgress />}
                {users.map((row, i) => (
                  <Box
                    key={i}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography sx={{ color: "primary.main", fontWeight: 600 }}>
                      {row?.profile?.first_name + " " + row?.profile?.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row?.profile?.email || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row?.profile?.phone || "-"}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {displayedRoles.map((role, j) => {
                        const key = `${row.id}-${role.name}`;
                        const serverHasRole = row.roles?.some(
                          (userRole) => userRole.role.name === role.name,
                        );
                        const hasRole =
                          optimisticState[key] !== undefined
                            ? optimisticState[key]
                            : serverHasRole;
                        return (
                          <Box
                            key={j}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderTop: "1px solid",
                              borderColor: "divider",
                              pt: 0.75,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                variant="body2"
                                sx={{ textTransform: "capitalize" }}
                              >
                                {role.name}
                              </Typography>
                              <RoleTooltip role={role} />
                            </Box>
                            <Checkbox
                              checked={hasRole}
                              sx={{
                                color: "primary.safeOnLight",
                                "&.Mui-checked": {
                                  color: "primary.safeOnLight",
                                },
                              }}
                              onChange={() => handleRoleChangeClick(row, role, hasRole, key)}
                              size="small"
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                {(isLoadingRoles || isLoadingUsers) && <LinearProgress />}
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey " }}>
                      <StyledTableCell>NAME</StyledTableCell>
                      <StyledTableCell>EMAIL</StyledTableCell>
                      <StyledTableCell>PHONE</StyledTableCell>
                      {displayedRoles.map((role, i) => (
                        <StyledTableCell key={i}>
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ textTransform: "capitalize" }}>
                            {role.name}
                            <RoleTooltip role={role} />
                          </Box>
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((row, i) => (
                      <StyledTableRow key={i}>
                        <StyledTableCell component="th" scope="row">
                          {row?.profile?.first_name +
                            " " +
                            row?.profile?.last_name}
                        </StyledTableCell>
                        <StyledTableCell>{row?.profile?.email}</StyledTableCell>
                        <StyledTableCell>{row?.profile?.phone}</StyledTableCell>
                        {displayedRoles.map((role, j) => {
                          const key = `${row.id}-${role.name}`;
                          const serverHasRole = row.roles?.some(
                            (userRole) => userRole.role.name === role.name,
                          );
                          const hasRole =
                            optimisticState[key] !== undefined
                              ? optimisticState[key]
                              : serverHasRole;
                          return (
                            <StyledTableCell key={j}>
                              <Checkbox
                                checked={hasRole}
                                sx={{
                                  color: "primary.safeOnLight",
                                  "&.Mui-checked": {
                                    color: "primary.safeOnLight",
                                  },
                                }}
                                onChange={() => handleRoleChangeClick(row, role, hasRole, key)}
                                size="small"
                              />
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              component="div"
              count={pagination?.total_items ?? 0}
              page={page - 1}
              rowsPerPage={PER_PAGE}
              rowsPerPageOptions={[PER_PAGE]}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              sx={{
                ".MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  minWidth: 0,
                  px: 0,
                },
                ".MuiTablePagination-spacer": { display: "none" },
              }}
            />
          </StyledPaper>
        </Grid>
      </Grid>
    </>
  );
}

export default Roles;
